import { useEffect, useRef, type JSX } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';

import { Footer } from '@/components/layout/Footer';
import { Header } from '@/components/layout/Header';
import { MAIN_CONTENT_ID, SkipLink } from '@/components/layout/SkipLink';
import { getRouteMeta } from '@/seo/routeMeta';

/**
 * In a single-page app nothing tells assistive technology that the page
 * changed. Moving focus to `main` and announcing the new document title
 * restores the behaviour a full page load would have given for free.
 */
function useRouteChangeAnnouncement(mainRef: React.RefObject<HTMLElement | null>): string {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [pathname, mainRef]);

  return getRouteMeta(pathname).title;
}

export function AppShell(): JSX.Element {
  const mainRef = useRef<HTMLElement>(null);
  const announcement = useRouteChangeAnnouncement(mainRef);

  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      <Header />
      <main id={MAIN_CONTENT_ID} ref={mainRef} tabIndex={-1} className="flex-1 focus:outline-none">
        <Outlet />
      </main>
      <Footer />
      <ScrollRestoration />
      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
